import React, { useState, useEffect, useRef } from 'react';
import { Card, Switch, Input, Space, Typography, Button, Tag, message, Modal, Upload, Radio } from 'antd';
import { SyncOutlined, PlusOutlined, DeleteOutlined, UploadOutlined, DownloadOutlined } from '@ant-design/icons';
import { TopKeyword, TopKeywordsConfig } from '../types/website';

const { Text } = Typography;

interface TopKeywordsBuilderProps {
  config: TopKeywordsConfig;
  onConfigChange: (config: TopKeywordsConfig) => void;
}

const TopKeywordsBuilder: React.FC<TopKeywordsBuilderProps> = ({ config, onConfigChange }) => {
  const [titleValue, setTitleValue] = useState<string>(config.title || 'Top từ khóa:');
  const titleDebounceRef = useRef<NodeJS.Timeout | null>(null);
  const [editingKeyword, setEditingKeyword] = useState<TopKeyword | null>(null);
  const [newKeywordText, setNewKeywordText] = useState('');
  const [newKeywordLink, setNewKeywordLink] = useState('');
  const [isUploadModalVisible, setIsUploadModalVisible] = useState(false);
  const [uploadMode, setUploadMode] = useState<'insert_and_update_duplicate' | 'replace_all'>('insert_and_update_duplicate');

  // Sync titleValue with config
  useEffect(() => {
    setTitleValue(config.title || 'Top từ khóa:');
  }, [config.title]);

  // Cleanup debounce
  useEffect(() => {
    return () => {
      if (titleDebounceRef.current) {
        clearTimeout(titleDebounceRef.current);
      }
    };
  }, []);

  const handleEnabledChange = (enabled: boolean) => {
    onConfigChange({
      ...config,
      enabled,
    });
  };

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setTitleValue(newValue);
    
    if (titleDebounceRef.current) {
      clearTimeout(titleDebounceRef.current);
    }
    
    titleDebounceRef.current = setTimeout(() => {
      onConfigChange({
        ...config,
        title: newValue,
      });
    }, 500);
  };

  const generateRandomKeywords = () => {
    const sampleKeywords = [
      'việt nam', 'học sinh', 'sinh viên', 'hot girl', 'gái xinh',
      'clip nóng', 'livestream', 'tiktok', 'facebook', 'zalo',
      'telegram', 'twitter', 'instagram', 'youtube', 'phim',
      'video', 'hình ảnh', 'ảnh nóng', 'gái gọi', 'massage',
      'spa', 'karaoke', 'bar', 'club', 'vũ trường',
      'người mẫu', 'ca sĩ', 'diễn viên', 'MC', 'hot boy',
      'idol', 'streamer', 'gamer', 'tiktoker', 'youtuber',
      'bạn gái', 'người yêu', 'vợ', 'chồng', 'crush',
      'em gái', 'chị gái', 'cô giáo', 'y tá', 'tiếp viên',
      'lộ clip', 'full HD', '4K', 'không che', 'mới nhất'
    ];

    // Shuffle and pick 25 random keywords
    const shuffled = [...sampleKeywords].sort(() => Math.random() - 0.5);
    const selected = shuffled.slice(0, 25);

    const newKeywords: TopKeyword[] = selected.map((text, index) => ({
      id: `keyword_${Date.now()}_${index}`,
      text: text,
      link: `/search?q=${encodeURIComponent(text)}`,
    }));

    onConfigChange({
      ...config,
      keywords: newKeywords,
    });
    message.success('Generated 25 random keywords');
  };

  const shuffleKeywords = () => {
    if (!config.keywords || config.keywords.length === 0) {
      message.warning('No keywords to shuffle');
      return;
    }
    
    const shuffled = [...config.keywords].sort(() => Math.random() - 0.5);
    onConfigChange({
      ...config,
      keywords: shuffled,
    });
    message.success('Keywords shuffled');
  };

  const handleAddKeyword = () => {
    if (!newKeywordText.trim()) {
      message.warning('Please enter a keyword');
      return;
    }
    
    const newKeyword: TopKeyword = {
      id: `keyword_${Date.now()}`,
      text: newKeywordText.trim(),
      link: newKeywordLink.trim() || `/search?q=${encodeURIComponent(newKeywordText.trim())}`,
    };

    onConfigChange({
      ...config,
      keywords: [...(config.keywords || []), newKeyword],
    });
    
    setNewKeywordText('');
    setNewKeywordLink('');
    message.success('Keyword added');
  };

  const handleDeleteKeyword = (keywordId: string) => {
    onConfigChange({
      ...config,
      keywords: (config.keywords || []).filter(k => k.id !== keywordId),
    });
  };

  const handleEditKeyword = (keyword: TopKeyword) => {
    setEditingKeyword(keyword);
    setNewKeywordText(keyword.text);
    setNewKeywordLink(keyword.link);
  };

  const handleSaveEditKeyword = () => {
    if (!editingKeyword || !newKeywordText.trim()) {
      message.warning('Please enter a keyword');
      return;
    }

    onConfigChange({
      ...config,
      keywords: (config.keywords || []).map(k => 
        k.id === editingKeyword.id 
          ? { ...k, text: newKeywordText.trim(), link: newKeywordLink.trim() || `/search?q=${encodeURIComponent(newKeywordText.trim())}` }
          : k
      ),
    });

    setEditingKeyword(null);
    setNewKeywordText('');
    setNewKeywordLink('');
    message.success('Keyword updated');
  };

  const handleCancelEdit = () => {
    setEditingKeyword(null);
    setNewKeywordText('');
    setNewKeywordLink('');
  };

  const handleUploadCSV = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e.target?.result as string;
        const lines = text.split('\n').filter(line => line.trim());
        
        if (lines.length < 2) {
          message.error('CSV file must have at least a header row and one data row');
          return;
        }

        // Parse CSV header
        const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
        const textIndex = headers.findIndex(h => h === 'text' || h === 'keyword');
        const linkIndex = headers.findIndex(h => h === 'link' || h === 'url');

        if (textIndex === -1) {
          message.error('CSV must have "text" or "keyword" column');
          return;
        }

        const newKeywords: TopKeyword[] = [];
        
        for (let i = 1; i < lines.length; i++) {
          const line = lines[i].trim();
          if (!line) continue;

          // Handle CSV with quoted values
          const values = line.match(/(".*?"|[^,]+)(?=\s*,|\s*$)/g)?.map(v => v.trim().replace(/^"|"$/g, '')) || line.split(',');
          
          if (values.length > textIndex) {
            const text = values[textIndex]?.trim();
            const link = linkIndex !== -1 && values[linkIndex] 
              ? values[linkIndex].trim() 
              : `/search?q=${encodeURIComponent(text)}`;

            if (text) {
              newKeywords.push({
                id: `keyword_${Date.now()}_${i}`,
                text: text,
                link: link,
              });
            }
          }
        }

        if (newKeywords.length === 0) {
          message.error('No valid keywords found in CSV file');
          return;
        }

        let updatedKeywords: TopKeyword[];
        let messageText = '';

        if (uploadMode === 'replace_all') {
          // Replace all existing keywords with new ones
          updatedKeywords = newKeywords;
          messageText = `Replaced all keywords with ${newKeywords.length} keyword(s)`;
        } else {
          // insert_and_update_duplicate: Insert new keywords, update duplicates based on text
          updatedKeywords = [...(config.keywords || [])];
          let insertedCount = 0;
          let updatedCount = 0;

          for (const newKw of newKeywords) {
            const existingIndex = updatedKeywords.findIndex(kw => kw.text.toLowerCase() === newKw.text.toLowerCase());
            if (existingIndex !== -1) {
              // Update existing keyword
              updatedKeywords[existingIndex] = {
                ...updatedKeywords[existingIndex],
                link: newKw.link,
              };
              updatedCount++;
            } else {
              // Insert new keyword
              updatedKeywords.push(newKw);
              insertedCount++;
            }
          }

          messageText = `Imported ${newKeywords.length} keyword(s): ${insertedCount} inserted, ${updatedCount} updated`;
        }

        onConfigChange({
          ...config,
          keywords: updatedKeywords,
        });
        
        setIsUploadModalVisible(false);
        message.success(messageText);
      } catch (error: any) {
        console.error('Failed to parse CSV:', error);
        message.error(`Failed to parse CSV file: ${error.message}`);
      }
    };
    reader.readAsText(file);
  };

  const handleDownloadSampleCSV = () => {
    const csvContent = `text,link
việt nam,/search?q=viet-nam
học sinh,/search?q=hoc-sinh
hot girl,/search?q=hot-girl
livestream,/search?q=livestream
tiktok,/search?q=tiktok`;

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'top_keywords_sample.csv';
    link.click();
    URL.revokeObjectURL(link.href);
  };

  return (
    <Card title="Top Keywords" style={{ marginBottom: 16 }}>
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        {/* Enable/Disable Toggle */}
        <Space>
          <Switch
            checked={config.enabled}
            onChange={handleEnabledChange}
            checkedChildren="Enabled"
            unCheckedChildren="Disabled"
          />
          <Text>Enable Top Keywords</Text>
        </Space>

        {config.enabled && (
          <div>
            {/* Title Input */}
            <div style={{ marginBottom: 12 }}>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>
                Title:
              </Text>
              <Input
                placeholder="Enter title (e.g., Top từ khóa:)"
                value={titleValue}
                onChange={handleTitleChange}
                style={{ width: '100%' }}
              />
            </div>

            {/* Action Buttons */}
            <Space style={{ marginBottom: 12 }} wrap>
              <Button 
                type="primary" 
                icon={<SyncOutlined />}
                onClick={generateRandomKeywords}
              >
                Random 25 Keywords
              </Button>
              <Button 
                icon={<SyncOutlined spin={false} />}
                onClick={shuffleKeywords}
                disabled={!config.keywords || config.keywords.length === 0}
              >
                Shuffle
              </Button>
              <Button 
                icon={<UploadOutlined />}
                onClick={() => setIsUploadModalVisible(true)}
              >
                Upload CSV
              </Button>
            </Space>

            {/* Add/Edit Keyword Form */}
            <div style={{ 
              marginBottom: 12, 
              padding: 12, 
              backgroundColor: 'var(--bg-secondary)', 
              borderRadius: 6 
            }}>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>
                {editingKeyword ? 'Edit Keyword:' : 'Add New Keyword:'}
              </Text>
              <Space direction="vertical" style={{ width: '100%' }}>
                <Input
                  placeholder="Keyword text"
                  value={newKeywordText}
                  onChange={(e) => setNewKeywordText(e.target.value)}
                />
                <Input
                  placeholder="Link (leave empty for auto /search?q=...)"
                  value={newKeywordLink}
                  onChange={(e) => setNewKeywordLink(e.target.value)}
                />
                <Space>
                  {editingKeyword ? (
                    <>
                      <Button type="primary" onClick={handleSaveEditKeyword}>
                        Save
                      </Button>
                      <Button onClick={handleCancelEdit}>
                        Cancel
                      </Button>
                    </>
                  ) : (
                    <Button type="primary" icon={<PlusOutlined />} onClick={handleAddKeyword}>
                      Add
                    </Button>
                  )}
                </Space>
              </Space>
            </div>

            {/* Keywords List */}
            <div>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>
                Keywords List ({config.keywords?.length || 0}):
              </Text>
              <div style={{ 
                maxHeight: 300, 
                overflowY: 'auto', 
                padding: 8, 
                backgroundColor: '#1a1a2e', 
                borderRadius: 6 
              }}>
                {(!config.keywords || config.keywords.length === 0) ? (
                  <Text type="secondary" style={{ color: '#888' }}>
                    No keywords yet. Click "Random 25 Keywords" to generate.
                  </Text>
                ) : (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {config.keywords.map((keyword) => (
                      <Tag
                        key={keyword.id}
                        style={{ 
                          padding: '4px 8px', 
                          borderRadius: 16,
                          backgroundColor: '#2a2a3e',
                          border: '1px solid #3a3a4e',
                          color: '#fff',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 4
                        }}
                      >
                        <span onClick={() => handleEditKeyword(keyword)} style={{ cursor: 'pointer' }}>
                          {keyword.text}
                        </span>
                        <DeleteOutlined 
                          style={{ fontSize: 10, color: '#ff4d4f', cursor: 'pointer' }}
                          onClick={() => handleDeleteKeyword(keyword.id)}
                        />
                      </Tag>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </Space>

      {/* Upload CSV Modal */}
      <Modal
        title="Upload Keywords CSV"
        open={isUploadModalVisible}
        onCancel={() => setIsUploadModalVisible(false)}
        footer={null}
        width={500}
      >
        <div style={{ marginBottom: 16 }}>
          <p>Upload a CSV file to import keywords.</p>
          <p><strong>Required column:</strong> text (or keyword)</p>
          <p><strong>Optional column:</strong> link (or url)</p>
          
          <div style={{ marginTop: 16, marginBottom: 16 }}>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 'bold' }}>
              Import Mode:
            </label>
            <Radio.Group
              value={uploadMode}
              onChange={(e) => setUploadMode(e.target.value)}
              style={{ width: '100%' }}
            >
              <Space direction="vertical">
                <Radio value="insert_and_update_duplicate">
                  <div>
                    <strong>Insert and Update Duplicate</strong>
                    <div style={{ fontSize: '12px', color: '#666', marginTop: 4 }}>
                      Insert new keywords. If a keyword with the same text exists, update its link.
                    </div>
                  </div>
                </Radio>
                <Radio value="replace_all">
                  <div>
                    <strong>Replace All</strong>
                    <div style={{ fontSize: '12px', color: '#666', marginTop: 4 }}>
                      Remove all existing keywords and replace with keywords from CSV.
                    </div>
                  </div>
                </Radio>
              </Space>
            </Radio.Group>
          </div>

          <div style={{ marginTop: 12, padding: 12, backgroundColor: '#f0f0f0', borderRadius: 4, fontSize: '12px' }}>
            <p style={{ margin: 0, marginBottom: 8 }}><strong>Note:</strong></p>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              <li>If link column is empty, auto-generates <code>/search?q=...</code></li>
              <li>Duplicate detection is based on <strong>text</strong> (case-insensitive)</li>
            </ul>
          </div>
        </div>
        
        <Upload.Dragger
          accept=".csv"
          beforeUpload={(file) => {
            handleUploadCSV(file);
            return false;
          }}
          showUploadList={false}
        >
          <p className="ant-upload-drag-icon">
            <UploadOutlined />
          </p>
          <p className="ant-upload-text">Click or drag CSV file to upload</p>
          <p className="ant-upload-hint">Supports .csv files only</p>
        </Upload.Dragger>

        <div style={{ marginTop: 16, textAlign: 'center' }}>
          <Button 
            icon={<DownloadOutlined />}
            onClick={handleDownloadSampleCSV}
          >
            Download sample CSV file
          </Button>
        </div>
      </Modal>
    </Card>
  );
};

export default TopKeywordsBuilder;

